import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RevenueImpact } from "./TreAssi";
import { api } from "@/lib/leuteliaApi";
import { isDemo } from "@/lib/demo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileText, Loader2, RefreshCw, ArrowRight, ShieldCheck, AlertTriangle, ListChecks, Download, Camera, Mail } from "lucide-react";
import InviaReferto from "./InviaReferto";
import { downloadPdfWithCharts } from "./chartCapture";

const PRI = { ALTA: "bg-red-100 text-red-700 border-red-300", MEDIA: "bg-amber-50 text-amber-800 border-amber-300", BASSA: "bg-emerald-50 text-emerald-700 border-emerald-300" };
const SHOT = { tenere: "#16493A", rifare: "#C2662E", eliminare: "#B0452E", nuovo: "#C6902A" };

function ShootingPlan({ shots }) {
  return (
    <div className="bg-white rounded-xl border border-brand-gold/25 p-5" data-testid="report-shooting-plan">
      <div className="flex items-center gap-2 mb-1"><Camera size={18} className="text-brand-gold" /><h4 className="font-serif text-[20px] text-brand-ink">Piano di shooting</h4></div>
      <p className="text-[12.5px] text-brand-muted mb-4">Tutte le critiche delle singole fotografie riunite in un unico piano: cosa tenere, rifare, eliminare e fotografare ex novo.</p>
      <div className="space-y-3">{shots.map((s, i) => (
        <div key={i} className="flex gap-4 rounded-lg bg-brand-cream/60 border border-brand-gold/20 p-3" data-testid={`report-shot-${i}`}>
          {s.photo_ref && <img src={s.photo_ref} alt="" className="w-[110px] h-[80px] object-cover rounded-md shrink-0 hidden sm:block" loading="lazy" />}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white uppercase" style={{ background: SHOT[s.status] || "#6F6A5F" }}>{s.status}</span><span className="font-serif text-[16px] text-brand-ink">{s.subject}</span></div>
            <p className="text-[12.5px] text-brand-ink mt-1">{s.why}</p>
            <p className="text-[12px] text-brand-muted mt-1.5"><strong className="text-brand-ink">Brief per il fotografo:</strong> {s.brief}</p>
          </div>
        </div>))}</div>
    </div>
  );
}

export function useReport(module, scope, version) {
  const [data, setData] = useState(null);
  const timer = useRef(null);
  const load = useCallback(async () => {
    const d = await api.report(scope.tenant, scope.hotel, module); setData(d);
    if (d.report?.status === "pending") timer.current = setTimeout(load, 5000);
    return d;
  }, [scope.tenant, scope.hotel, module]);
  useEffect(() => { load().catch(() => {}); return () => clearTimeout(timer.current); }, [load, version]);
  const generate = async (force) => {
    try { await api.generateReport({ tenant: scope.tenant, hotel: scope.hotel, module, force }); toast.message("LEUTELIA sta scrivendo il referto completo (2-4 minuti)…"); load(); }
    catch (e) { toast.error(e.message); }
  };
  return { data, generate, reload: load };
}

export function ReportBody({ r, testid = "report-body" }) {
  return (
    <div className="space-y-6" data-testid={testid}>
      <div className="bg-white rounded-xl border border-brand-gold/25 p-5 flex flex-wrap gap-5 items-center">
        <div className="w-20 h-20 rounded-full border-4 flex items-center justify-center shrink-0" style={{ borderColor: r.score >= 70 ? "#16493A" : r.score >= 40 ? "#C6902A" : "#B0452E" }}><span className="font-serif text-[26px] text-brand-ink">{r.score}</span></div>
        <div className="flex-1 min-w-[240px]"><div className="text-[11px] font-bold uppercase tracking-wide text-brand-muted">Sintesi per il direttore</div><p className="text-[14px] text-brand-ink leading-relaxed mt-1">{r.executive_summary}</p><p className="text-[12px] text-brand-muted mt-1 italic">{r.score_reason}</p></div>
      </div>
      <RevenueImpact r={r} testid={`${testid}-revenue`} />
      {r.sections?.map((s, i) => (
        <div key={i} className="bg-white rounded-xl border border-brand-gold/25 p-5" data-testid={`report-section-${i}`}>
          <h4 className="font-serif text-[20px] text-brand-ink">{i + 1}. {String(s.heading || "").replace(/^\d+[.)]\s*/, "")}</h4>
          {(s.body || "").split(/\n+/).map((p, j) => <p key={j} className="text-[13.5px] text-brand-ink leading-relaxed mt-2">{p}</p>)}
          {s.findings?.length > 0 && <ul className="mt-3 space-y-1">{s.findings.map((f, j) => <li key={j} className="flex items-start gap-2 text-[13px] text-brand-ink"><ListChecks size={14} className="text-brand-gold mt-0.5 shrink-0" />{f}</li>)}</ul>}
          {s.evidence?.length > 0 && <div className="mt-2 text-[11px] text-brand-muted">Prove: {s.evidence.join(" · ")}</div>}
        </div>
      ))}
      <div className="bg-brand-green text-brand-cream rounded-xl p-5">
        <div className="text-brand-gold font-bold tracking-[0.18em] text-[10.5px] uppercase mb-3">Piano d'azione</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{r.actions?.map((a, i) => (
          <div key={i} className="rounded-lg bg-brand-cream/[0.06] border border-brand-gold/20 p-4" data-testid={`report-action-${i}`}>
            <div className="flex items-center gap-2"><span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${PRI[a.priority] || PRI.MEDIA}`}>{a.priority}</span><span className="text-[11px] text-brand-cream/70">{a.horizon}</span></div>
            <div className="font-serif text-[16px] mt-1">{a.title}</div><p className="text-[12px] text-brand-cream/75 mt-1">{a.why}</p>
            <div className="flex items-start gap-1.5 mt-2 text-[12px] text-brand-goldSoft"><ArrowRight size={13} className="mt-0.5 shrink-0" />{a.how}</div>
          </div>))}</div>
      </div>
      {r.limits && <div className="flex items-start gap-2 text-[12.5px] text-brand-muted"><AlertTriangle size={15} className="text-brand-orange shrink-0 mt-0.5" /><span><strong>Limiti:</strong> {r.limits}</span></div>}
      {r.shots?.length > 0 && <ShootingPlan shots={r.shots} />}
    </div>
  );
}

export function ReportEvidence({ r }) {
  return (
    <div className="space-y-2" data-testid="report-evidence">
      {(r.evidence_index || []).map((e, i) => (
        <div key={i} className="bg-white rounded-lg border border-brand-gold/25 p-3 flex gap-3"><ShieldCheck size={16} className="text-brand-gold shrink-0 mt-0.5" /><div><div className="text-[12px] font-mono text-brand-muted">{e.id} · {e.source}</div><div className="text-[13px] text-brand-ink">{e.what}</div></div></div>
      ))}
      {!(r.evidence_index || []).length && <p className="text-[13px] text-brand-muted italic">Nessuna prova indicizzata.</p>}
    </div>
  );
}

export function ReportToolbar({ module, scope, r, pending, generate, onShare }) {
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdf = async () => {
    setPdfBusy(true);
    try {
      const n = await downloadPdfWithCharts(api.reportPdfUrl(scope.tenant, scope.hotel, module), `LEUTELIA_${module}_${scope.hotel}.pdf`, { tenant: scope.tenant, hotel: scope.hotel, module }, (m) => toast.message(m));
      toast.success(n ? `PDF pronto con ${n} grafici: lo trovi tra i download.` : "PDF pronto: lo trovi tra i download.");
    }
    catch (e) { toast.error(e.message); } finally { setPdfBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {r?.status === "ready" && (
        <>
        <Button data-testid={`report-email-${module}`} variant="outline" onClick={onShare} className="h-9 rounded-lg border-brand-green/30 text-brand-green text-[12.5px] font-semibold"><Mail size={14} className="mr-2" />Invia via email</Button>
        <Button data-testid={`report-pdf-${module}`} variant="outline" disabled={pdfBusy} onClick={pdf} className="h-9 rounded-lg border-brand-green/30 text-brand-green text-[12.5px] font-semibold">{pdfBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}Scarica PDF</Button>
        </>
      )}
      {!(isDemo() && r) && <Button data-testid={`report-generate-${module}`} disabled={pending} onClick={() => generate(!!r)} className="h-9 rounded-lg bg-brand-gold hover:bg-brand-goldSoft text-brand-greenDark text-[12.5px] font-semibold">
        {pending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}{pending ? "In scrittura…" : r ? "Rigenera" : "Genera referto"}
      </Button>}
    </div>
  );
}

export default function ReportViewer({ module, scope, version, title, open, onOpenChange, tab, setTab }) {
  const { data, generate } = useReport(module, scope, version);
  const r = data?.report;
  const pending = r?.status === "pending";
  const [shareOpen, setShareOpen] = useState(false);
  return (
    <>
    <InviaReferto module={module} title={title} scope={scope} open={shareOpen} onOpenChange={setShareOpen} />
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto leutelia-scroll bg-brand-cream" data-testid={`report-dialog-${module}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-brand-ink flex items-center gap-2"><FileText size={20} className="text-brand-gold" /> {r?.title || `${title} — Referto completo`}</DialogTitle>
          <DialogDescription>Referto LEUTELIA · versione {r?.understanding_version ?? version}{data?.stale ? " · da aggiornare con i dati più recenti" : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          {[["report", "Referto"], ["evidence", "Prove"]].map(([k, l]) => <button key={k} data-testid={`report-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border ${tab === k ? "bg-brand-green text-brand-cream border-brand-green" : "border-brand-gold/30 text-brand-muted"}`}>{l}</button>)}
          <div className="ml-auto"><ReportToolbar module={module} scope={scope} r={r} pending={pending} generate={generate} onShare={() => setShareOpen(true)} /></div>
        </div>
        {!r && <p className="text-[14px] text-brand-muted italic py-6" data-testid="report-empty">Nessun referto ancora scritto per questa versione. Genera il referto completo: LEUTELIA analizzerà in profondità tutte le fonti acquisite.</p>}
        {pending && <div className="space-y-2 py-6" data-testid="report-pending">{[95, 100, 80, 90].map((w, i) => <div key={i} className="h-3 rounded bg-brand-gold/20 animate-pulse" style={{ width: `${w}%` }} />)}</div>}
        {r?.status === "failed" && <p className="text-[13px] text-brand-maroon">Generazione non riuscita: {r.error}</p>}
        {r?.status === "ready" && tab === "report" && <ReportBody r={r} />}
        {r?.status === "ready" && tab === "evidence" && <ReportEvidence r={r} />}
      </DialogContent>
    </Dialog>
    </>
  );
}
